import React, { useEffect, useState } from "react";

import Modal from "./Modal";
import RoleDayWeightGrid from "@/components/sections/RoleDayWeightGrid";
import { createRoleHeaderDayUIItem } from "@/utils/roleDayGridUtils";
import { fetchEquityWeights, updateEquityWeights } from "@/api.ts";
import type { EquityWeights, Role } from "@/types.ts";

const DAYS = ["0", "1", "2", "3", "4", "5", "6"];
const DEFAULT_WEIGHT = 100;

const headerDays = DAYS.map((d) => createRoleHeaderDayUIItem(d));

export type EquityWeightsModalProps = {
    open: boolean;
    onClose: () => void;
    roles: Role[];
};

const EquityWeightsModal: React.FC<EquityWeightsModalProps> = ({ open, onClose, roles }) => {
    const [weights, setWeights] = useState<EquityWeights>({});
    const [loading, setLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setSaveError(null);

        fetchEquityWeights()
            .then((data) => {
                if (!cancelled) {
                    setWeights(data ?? {});
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("fetchEquityWeights failed", err);
                    setWeights({});
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open]);

    const handleWeightChange = (roleId: string, dayId: string, value: number) => {
        setWeights((prev) => ({
            ...prev,
            [roleId]: { ...(prev[roleId] ?? {}), [dayId]: value },
        }));
    };

    const handleConfirm = async () => {
        if (saving) {
            return;
        }

        setSaving(true);
        setSaveError(null);

        try {
            await updateEquityWeights(weights);
        } catch (err) {
            // Keep the modal open: closing here would discard the grid with no way to tell it failed.
            console.error("updateEquityWeights failed", err);
            setSaveError("Échec de la sauvegarde. Vérifie ta connexion et réessaie.");
            setSaving(false);
            return;
        }

        setSaving(false);
        onClose();
    };

    const rows = (roles ?? []).map((role) => ({ id: role.id, name: role.name }));

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Poids d'équité"
            size="xl"
            footer={
                <>
                    {saveError && (
                        <p className="mr-auto text-sm text-red-600">{saveError}</p>
                    )}
                    <button
                        onClick={onClose}
                        className="inline-flex items-center rounded-full bg-gray-200 cursor-pointer px-4 py-2 font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading || saving}
                        className={`inline-flex items-center rounded-full bg-green-600 px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${loading || saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-green-700"}`}
                    >
                        Confirmer
                    </button>
                </>
            }
        >
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"/>
                    Chargement…
                </div>
            ) : rows.length === 0 ? (
                <div className="py-10 text-center text-gray-500">Aucun rôle à pondérer.</div>
            ) : (
                <RoleDayWeightGrid
                    headerDays={headerDays}
                    rows={rows}
                    weights={weights}
                    defaultWeight={DEFAULT_WEIGHT}
                    onWeightChange={handleWeightChange}
                />
            )}
        </Modal>
    );
};

export default EquityWeightsModal;
